import { useEffect } from "react";

import { useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationOptions } from "@react-navigation/native-stack";
import { amountToCents, Currency, sanitizedAmountAsDecimal } from "currency";
import { Formik, FormikErrors, useFormikContext } from "formik";
import { ScrollView, View } from "react-native";
import {
  Card,
  IconButton,
  SegmentedButtons,
  useTheme,
} from "react-native-paper";
import { useTailwind } from "tailwind-rn";
import { parseInterval, Price, priceIntervalText } from "utils";

import { FloatingButton, Header, Input, Form } from "../../../components";
import Picker from "../../../components/Picker";
import DiscountablePriceInfo from "../components/DiscountablePriceInfo";
import { FormikFormSwitch } from "../components/FormSwitch";
import PriceInfoBanner from "../components/PriceInfoBanner";
import useProductFormAPI from "../contexts/ProductFormAPIContext";
import useProductFormFormik from "../hooks/useProductFormFormik";
import useProductFormNavigation from "../hooks/useProductFormNavigation";
import { StackScreenProps, productFormRoutes } from "../navigation/types";
import { PriceMode } from "../types";
import { confirmAlert, unsavedChangesAlert } from "../utils";

type Props = StackScreenProps<typeof productFormRoutes.priceForm>;
export default function PriceFormScreen({ route, navigation }: Props) {
  const { values, savePrice } = useProductFormFormik();
  const { currency } = useProductFormAPI();

  const idx = route.params?.selectedPriceIndex;
  const savedPrice = idx !== undefined ? values.prices[idx] : undefined;

  const initialFormValues = getInitialValuesFromPrice(savedPrice, currency);

  async function onSubmit(params: PriceFormValues) {
    const price = transformValues(params, currency);
    savePrice(price, idx);
    navigation.goBack();
  }

  async function onValidate(
    params: PriceFormValues
  ): Promise<FormikErrors<PriceFormValues>> {
    const price = transformValues(params, currency);
    const errors: FormikErrors<PriceFormValues> = {};

    if (
      price.amount_not_discounted &&
      price.amount_not_discounted <= price.amount
    ) {
      errors.amountNotDiscounted =
        "El monto antes de descuento debe ser mayor al monto actual.";
    }

    if (price.installments_count) {
      if (price.installments_count < 2) {
        errors.installmentsCount = "La cantidad de cuotas debe ser mayor a 1.";
      }

      if (price.amount % price.installments_count !== 0) {
        errors.amount =
          "El monto total debe ser divisible entre la cantidad de cuotas.";
      }
    }

    return errors;
  }

  return (
    <Formik
      initialValues={initialFormValues}
      onSubmit={onSubmit}
      validate={onValidate}
    >
      <PriceForm savedPrice={savedPrice} />
    </Formik>
  );
}

function transformValues(params: PriceFormValues, currency: Currency): Price {
  const planType: Price["payment_plan_type"] =
    params.priceMode === "one_off"
      ? "one_off"
      : params.recurrenceMode === "subscriptions"
      ? "recurring"
      : "installment";

  const installmentsCount =
    planType === "installment" ? params.installmentsCount : null;

  function cents(amt: string) {
    return Number(amountToCents({ amount: Number(amt), currency }));
  }

  const amount_not_discounted =
    planType === "installment" ? null : cents(params.amountNotDiscounted);

  const recurring_interval =
    planType === "one_off" ? null : params.recurringInterval;

  const recurring_interval_count =
    planType === "one_off" ? 0 : params.recurringIntervalCount;

  return {
    name: params.name,
    amount: cents(params.amount),
    amount_not_discounted,
    sold_out: params.soldOut,
    payment_plan_type: planType,
    recurring_interval,
    recurring_interval_count,
    installments_count: installmentsCount,
    uuid: params.uuid || undefined,
  };
}

type PriceFormValues = {
  priceMode: PriceMode;
  name: string;
  recurrenceMode: "subscriptions" | "installments";
  recurringInterval: Price["recurring_interval"];
  recurringIntervalCount: Price["recurring_interval_count"];
  installmentsCount: number;
  amount: string;
  amountNotDiscounted: string;
  soldOut: boolean;
  uuid: string;
};

function getInitialValuesFromPrice(
  price: Price | undefined,
  currency: Currency
): PriceFormValues {
  if (!price) return emptyPriceForm;

  function decimal(amount: number | null) {
    return amount ? sanitizedAmountAsDecimal(amount, currency) : "";
  }

  return {
    priceMode:
      price.payment_plan_type === "recurring" ||
      price.payment_plan_type === "installment"
        ? "recurring"
        : "one_off",
    name: price.name || "",
    recurrenceMode:
      price.payment_plan_type === "installment"
        ? "installments"
        : "subscriptions",
    recurringInterval: price.recurring_interval || null,
    recurringIntervalCount: price.recurring_interval_count || 0,
    installmentsCount: price.installments_count || 0,
    amount: decimal(price.amount),
    amountNotDiscounted: decimal(price.amount_not_discounted),
    soldOut: price.sold_out || false,
    uuid: price.uuid || "",
  };
}

const emptyPriceForm: PriceFormValues = {
  priceMode: "one_off",
  name: "",
  recurrenceMode: "subscriptions",
  recurringInterval: null,
  recurringIntervalCount: 0,
  installmentsCount: 0,
  amount: "",
  amountNotDiscounted: "",
  soldOut: false,
  uuid: "",
};

function PriceForm({ savedPrice }: { savedPrice: Price | undefined }) {
  const tailwind = useTailwind();
  const { currency } = useProductFormAPI();

  const navigation = useProductFormNavigation();
  const { values, errors, dirty, handleChange, setFieldValue } =
    usePricesFormik();

  useEffect(() => {
    navigation.setOptions({
      header: () => <PriceFormHeader isDirty={dirty} />,
    });
  }, [dirty]);

  const copy = getFormCopy(values);

  const isInstallment =
    values.priceMode === "recurring" &&
    values.recurrenceMode === "installments";

  const isEditing = !!savedPrice?.name;

  return (
    <View>
      <ScrollView>
        <PriceInfoBanner mode={values.priceMode} />
        <Card style={tailwind("m-3 rounded-xl mb-24")}>
          <Card.Content style={tailwind("gap-4")}>
            <Form.Field
              label="Tipo de precio"
              required
              info={copy.priceModeInfo}
              error={errors.priceMode}
            >
              <SegmentedButtons
                value={values.priceMode}
                onValueChange={handleChange("priceMode")}
                buttons={[
                  { value: "one_off", label: "Único", icon: "cart-outline" },
                  { value: "recurring", label: "Recurrente", icon: "sync" },
                ]}
                style={tailwind("mb-2")}
              />
            </Form.Field>

            <Form.Field
              label="Nombre del precio"
              required
              info="* Requerido. Visible en tu tienda."
              error={errors.name}
            >
              <Input.Formik.Text
                fieldName="name"
                placeholder={'E.g.: "Early bird" o "Plan platino"'}
              />
            </Form.Field>

            <Form.Field
              label="Tipo de precio recurrente"
              required
              info={copy.recurrenceModeInfo}
              error={errors.recurrenceMode}
              hidden={values.priceMode === "one_off"}
            >
              <SegmentedButtons
                value={values.recurrenceMode}
                onValueChange={handleChange("recurrenceMode")}
                buttons={[
                  {
                    value: "subscriptions",
                    label: "Suscripción",
                    icon: "sync",
                  },
                  {
                    value: "installments",
                    label: "Plan de pagos",
                    icon: "clock-outline",
                  },
                ]}
                style={tailwind("mb-2")}
              />
            </Form.Field>

            <Form.Field
              label={copy.amountLabel}
              required
              info={copy.amountInfo}
              error={errors.amount}
            >
              <Input.Formik.Currency fieldName="amount" currency={currency} />
            </Form.Field>

            <Form.Field
              label="Cantidad de cuotas"
              required
              error={errors.installmentsCount}
              hidden={!isInstallment}
              info="* Requerido. Número de cuotas para dividir el monto total del plan."
            >
              <Picker
                disableInputLabel
                options={installmentsCountOptions()}
                placeholder="Selecciona el número de cuotas"
                preselectedValue={values.installmentsCount.toString()}
                selectOption={(o) => {
                  setFieldValue("installmentsCount", Number(o.value));
                }}
              />
            </Form.Field>

            <PreviewInstallmentAmountInput
              hidden={!isInstallment}
              currency={currency}
            />

            <Form.Field
              label="Monto antes de descuento"
              info={
                <DiscountablePriceInfo
                  amount={values.amount}
                  amountNotDiscounted={values.amountNotDiscounted}
                  currency={currency}
                />
              }
              error={errors.amountNotDiscounted}
              hidden={isInstallment}
            >
              <Input.Formik.Currency
                fieldName="amountNotDiscounted"
                currency={currency}
              />
            </Form.Field>

            <Form.Field
              label="Período de conteo"
              required
              info="* Requerido. Elige un período de referencia para el cobro."
              error={errors.recurringInterval}
              hidden={values.priceMode === "one_off"}
            >
              <Picker
                disableInputLabel
                options={[
                  { value: "day", label: "día" },
                  { value: "week", label: "semana" },
                  { value: "month", label: "mes" },
                  { value: "year", label: "año" },
                ]}
                placeholder="Selecciona uno"
                preselectedValue={values.recurringInterval}
                selectOption={(o) => {
                  if (o.value) {
                    handleChange("recurringInterval")(o.value);
                  } else {
                    setFieldValue("recurringInterval", null);
                  }
                  setFieldValue("recurringIntervalCount", 1);
                }}
              />
            </Form.Field>

            <Form.Field
              label="Frecuencia para cobro de cuotas"
              required
              info={copy.recurringIntervalCountInfo}
              error={errors.recurringIntervalCount}
              hidden={values.priceMode === "one_off"}
            >
              <Picker
                disabled={values.recurringInterval === null}
                disableInputLabel
                options={recurringIntervalCountOptions(
                  values.recurringInterval
                )}
                placeholder="Selecciona periodo de conteo"
                preselectedValue={
                  values.recurringIntervalCount
                    ? values.recurringIntervalCount.toString()
                    : undefined
                }
                selectOption={(o) => {
                  setFieldValue("recurringIntervalCount", Number(o.value));
                }}
              />
            </Form.Field>
            <SoldOutSwitch />
          </Card.Content>
        </Card>
      </ScrollView>
      <SubmitButton isEditing={isEditing} />
    </View>
  );
}

function SoldOutSwitch() {
  const {
    values: { soldOut },
  } = usePricesFormik();

  const word = soldOut ? "disponible" : "agotado";
  const title = `Marcar ${word}`;
  const body = `Pulsa para marcar este precio como ${word}`;

  return <FormikFormSwitch fieldName="soldOut" title={title} body={body} />;
}

type PreviewInstallmentAmountInputProps = {
  currency: Currency;
  hidden: boolean;
};

function PreviewInstallmentAmountInput({
  currency,
  hidden,
}: PreviewInstallmentAmountInputProps) {
  const { values } = usePricesFormik();

  if (hidden) return null;

  const amt = values.installmentsCount
    ? sanitizedAmountAsDecimal(
        Number(
          amountToCents({
            amount: Number(values.amount),
            currency,
          })
        ) / values.installmentsCount,
        currency
      )
    : "";

  return (
    <Form.Field
      label="Monto de cada cuota"
      info="Este monto será cobrado cada período."
    >
      <Input.Currency currency={currency} value={amt} disabled />
    </Form.Field>
  );
}

function SubmitButton({ isEditing }: { isEditing: boolean }) {
  const { values, isValid, handleSubmit } = usePricesFormik();
  const text = `${isEditing ? "Editar" : "Crear"} precio`;

  const isOneOffInvalid = !values.amount || !values.name;

  const isSubInvalid =
    isOneOffInvalid ||
    !values.recurrenceMode ||
    !values.recurringIntervalCount ||
    !values.recurringInterval;

  const isInstallmentsInvalid = isSubInvalid || !values.installmentsCount;

  const disabled =
    !isValid ||
    (values.priceMode === "one_off"
      ? isOneOffInvalid
      : values.recurrenceMode === "subscriptions"
      ? isSubInvalid
      : isInstallmentsInvalid);

  return (
    <FloatingButton text={text} disabled={disabled} onPress={handleSubmit} />
  );
}

function usePricesFormik() {
  return useFormikContext<PriceFormValues>();
}

export const priceFormScreenOptions: NativeStackNavigationOptions = {
  header: () => <PriceFormHeader isDirty={false} />,
};

function PriceFormHeader({ isDirty }: { isDirty: boolean }) {
  const { colors } = useTheme();
  const { values, removePrice } = useProductFormFormik();

  const { goBack } = useNavigation<Props["navigation"]>();
  const { params } = useRoute<Props["route"]>();

  const idx = params?.selectedPriceIndex;
  const savedPrice = idx !== undefined ? values.prices[idx] : undefined;
  const isEditing = !!savedPrice?.name;

  const title = `${isEditing ? "Editar" : "Crear"} precio`;

  function onDeleteConfirm() {
    removePrice(params?.selectedPriceIndex);
    goBack();
  }

  function handleDelete() {
    confirmAlert({
      title: "Eliminar precio",
      body: "¿Estás seguro que deseas eliminar este precio?",
      cancelButton: { text: "No", onPress: undefined },
      confirmButton: { text: "Sí", onPress: onDeleteConfirm },
    });
  }

  function onBackPress() {
    unsavedChangesAlert({ isDirty, goBack });
  }

  return (
    <Header
      title={title}
      onBackPress={onBackPress}
      onClosePress={onBackPress}
      rightAction={
        savedPrice?.uuid ? (
          <IconButton
            icon="delete"
            iconColor={colors.error}
            onPress={handleDelete}
          />
        ) : undefined
      }
    />
  );
}

function installmentsCountOptions() {
  function opt(n: number) {
    return { label: `${n} cuotas`, value: n.toString() };
  }

  return [
    opt(2),
    opt(3),
    opt(4),
    opt(5),
    opt(6),
    opt(7),
    opt(8),
    opt(9),
    opt(10),
    opt(11),
    opt(12),
    opt(24),
  ];
}

function recurringIntervalCountOptions(
  recurring_interval: Price["recurring_interval"]
): Array<{ label: string; value: string }> {
  function opt(recurring_interval_count: number) {
    return {
      label: priceIntervalText({
        recurring_interval,
        recurring_interval_count,
      }),
      value: recurring_interval_count.toString(),
    };
  }

  switch (recurring_interval) {
    case "year":
      return [opt(1)];
    case "month":
      return [opt(1), opt(2), opt(3), opt(4), opt(5), opt(6)];
    case "week":
      return [opt(1), opt(2), opt(3), opt(4)];
    case "day":
      return [opt(1), opt(2), opt(3), opt(4), opt(5), opt(6), opt(7)];
    default:
      return [opt(1), opt(2), opt(3), opt(4), opt(5), opt(6)];
  }
}

function getFormCopy(values: PriceFormValues) {
  const frequencyInfo =
    values.recurringIntervalCount > 0 && values.recurringInterval
      ? `* Requerido. Se cobrará una cuota cuando pase la frecuencia definida: cada [${
          values.recurringIntervalCount
        }] [${parseInterval(values.recurringInterval)}]`
      : "* Requerido. Se cobrará una cuota cuando pase la frecuencia definida.";

  if (values.priceMode === "one_off") {
    return {
      priceModeInfo:
        "Este precio único se cobrará una sola vez al momento de la compra.",
      amountLabel: "Monto",
      amountInfo: "* Requerido. El monto que se cobrará por el producto",
      frequencyInfo,
    };
  } else {
    const priceModeInfo =
      "Este precio se cobrará de forma recurrente según definas debajo.";

    const recurringIntervalCountInfo = `* Requerido. ${
      values.recurringInterval === null
        ? "Selecciona primero un periodo de conteo"
        : `Se cobrara una cuota ${priceIntervalText({
            recurring_interval: values.recurringInterval,
            recurring_interval_count: values.recurringIntervalCount,
          })}`
    }`;

    if (values.recurrenceMode === "subscriptions") {
      return {
        priceModeInfo,
        recurrenceModeInfo:
          "Una suscripción continuará en el tiempo hasta que tú o tu cliente la decidan cancelar.",
        amountLabel: "Monto de cada cuota",
        amountInfo: "* Requerido. Este monto será cobrado cada periodo.",
        frequencyInfo,
        recurringIntervalCountInfo,
      };
    } else {
      return {
        priceModeInfo,
        recurrenceModeInfo:
          "Un plan de pagos tiene fecha de terminación y sólo tú la puedes cancelar (salvo que la tarjeta de tu cliente no tenga fondos o venza).",
        amountLabel: "Monto total del plan",
        amountInfo:
          "* Requerido. Este monto será dividido en las cuotas que definas.",
        frequencyInfo,
        recurringIntervalCountInfo,
      };
    }
  }
}
